import { Box, ClickAwayListener, Grid, Typography } from '@mui/material'
import _ from 'lodash'
import moment from 'moment'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Dropdown } from 'react-bootstrap'
import { useHistory } from 'react-router-dom'
import * as XLSX from "xlsx"
import { forceTextOnDateCellsXlsx } from 'utils/excel.utils'

/** TRANSLATION*/
import { useTranslation } from 'react-i18next'

/**API */
import ClinicApi from 'api/master/clinic.api'
import ReportApi from 'api/report.api'
import { getBranch, numberFormat } from 'utils/app.utils'
import { dateTimeToView, dateToView } from 'utils/date.utils'

/**COMPONENT */
import HeaderCard from 'component/CardCustom/HeaderCard'
import InputSecondNewRangePicker, { emptyRangePicker } from 'component/Input/InputSecondNewRangePicker'
import Loading from 'component/Loading'

/**STYLE */
import ComponentApi from 'api/components-api'
import SearchPatients from 'component/SearchPatients'
import * as UseStyled from 'features/report/useStyled'

/**STYLE */
import InputCheckbox from 'component/Input/InputCheckbox'
import GoogleExcelSelectSearch from 'component/Search/GoogleExcelSelectSearch'
import AutocompleteSelect from 'component/Select/AutocompleteSelect'
import { colors } from 'constants/theme'
import { CustomFilterDoctor, ListFilterDoctors } from 'features/counter/appointment/head-appointment'
import MainButton from 'new-components/buttons/main-button'
import SelectInput from 'new-components/inputs/select-input'
import { UseSnCode } from 'utils/useSnCode'
import AccountingList from './AccountingList'

type Doctors = {
  userId: number
  firstname: string
  lastname: string
  fullname: string
}

export interface DentistInterface {
  dentistId: number
  dentistFullname: string
}

export default function Accounting() {
  const { t } = useTranslation()
  const history = useHistory()
  const componentRef = useRef<HTMLDivElement>(null)
  const momentNowRange = [moment().format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')]
  const momentDate = () => momentNowRange
  const [branchId, setBranchId] = useState<any>(getBranch())
  const [branches, setBranches] = useState<any[]>([])
  const [operativeCode, setOperativeCode] = useState<any[]>([])
  const [rowCount, setRowCount] = useState<number>(0)
  const [sortBy, setSortBy] = useState<string>('createAt')
  const [sortType, setSortType] = useState<string>('ASC')
  const [doctors, setDoctors] = useState<Doctors[]>([])
  const [searchTrigger, setSearchTrigger] = useState(0) // ใช้เป็น trigger สำหรับค้นหา
  const [rows, setRows] = useState<any[]>([])
  const [rowsExport, setRowsExport] = useState<any[]>([])
  const [overdue, setOverdue] = useState<number>(0)
  const [rangeDate, setRangeDate] = useState<string[]>(momentDate)
  const [page, setPage] = useState<number>(1)
  const [pageLimit, setPageLimit] = useState<number>(10)
  const [loading, setLoading] = useState<boolean>(false) // เริ่มต้นเป็น false เพราะยังไม่โหลด
  const [exporting, setExporting] = useState<boolean>(false)
  const [renderExporting, setRenderExporting] = useState<boolean>(false)
  const [readyToExport, setReadyToExport] = useState<boolean>(false)
  const [selectDoctors, setSelectDoctors] = useState<any[]>([])
  const [billStatus, setBillStatus] = useState<string>('ALL')
  const [patientFilter, setPatientFilter] = useState<string>(t('REPORT.REPORT_GROUP.ALL'))
  const [patientId, setPatientId] = useState<number>(0)
  // const [codeForm, setCodeForm] = useState<{ operativeCode: string }>({ operativeCode: '' })
  const [codeForm, setCodeForm]: any = useState(null)
  const [codeTo, setCodeTo] = useState<{ operativeCode: string }>({ operativeCode: '' })
  const [doctorsMultiple, setDoctorsMultiple] = useState<DentistInterface[]>([])
  const [openFilterDoctors, setOpenFilterDoctors] = useState(false)
  const [fetch, setFetch] = useState(false)

  const [snType, setSnType] = useState(false)

  const handleSnCode = useCallback(() => {
    const fetchData = async () => {
      const sn = await UseSnCode()
      setSnType(sn)
    }

    fetchData()
  }, [snType])

  useEffect(() => {
    handleSnCode()
  }, [])

  const getFilterUser = useCallback(() => {
    if (_.includes(selectDoctors, 'ALL')) return ` ${t('REPORT.REPORT_GROUP.ALL')}`
    else {
      return ` ${doctorsMultiple
        .filter((d: DentistInterface) => selectDoctors.includes(d.dentistId))
        ?.map((d: DentistInterface) => d.dentistFullname)
        .join(', ')}`
    }
  }, [selectDoctors, doctorsMultiple, t])

  const verifyPermission = async () => {
    try {
      const [branchesRes, itemCodeRes] = await Promise.all([
        ClinicApi.findAllBranches(),
        ComponentApi.findAllItemCode(),
      ])

      setBranches(branchesRes.data)
      setOperativeCode(itemCodeRes.data)

    } catch (e) {
      console.error('Error loading initial data:', e)
    }
  }
  const loadDoctor = async (branchId: number) => {
    setLoading(true)
    try {
      const doctorsRes = await ClinicApi.findAllDoctors('', '', rangeDate[0], rangeDate[1], branchId)

      const doctorsWithDefault = [{
        certificate: "",
        firstname: "ไม่ระบุ",
        fullname: "ไม่ระบุ",
        lastname: "ไม่ระบุ",
        position: "ทันตแพทย์",
        userId: 999999
      }, ...doctorsRes.data]

      setDoctors(doctorsWithDefault)
      setSelectDoctors(['ALL', 999999, ...doctorsRes.data.map((d: any) => d.userId)])
      setFetch(true)
    } catch (e) {
      console.error('Error loading initial data:', e)
      setLoading(false)
    }
  }

  useEffect(() => {
    verifyPermission()
  }, [])
  useEffect(() => {
    loadDoctor(branchId)
  }, [branchId])

  // ดึงข้อมูลสำหรับแสดงผล (มี pagination)
  const fetchData = useCallback(async () => {
    setLoading(true)
    // setRows([])
    // setRowCount(0)
    let condition: any = {
      page: page,
      pageLimit: pageLimit,
      branchId: branchId,
      dateStart: rangeDate[0],
      dateEnd: rangeDate[1],
      code: codeForm && codeForm.length ? _.map(codeForm, 'value').join(',') : '',
      billStatus: billStatus === 'ALL' ? '' : billStatus,
      // export: 1
      // ไม่ใส่ export: 1 เพื่อให้ได้ pagination
    }
    if (selectDoctors.length) {
      const doctor = _.filter(selectDoctors, (v: any) => v > 0).join(',')
      if (doctor) condition = { ...condition, doctorId: doctor }
    }
    if (patientId) condition = { ...condition, patientId: patientId }
    if (sortBy) condition = { ...condition, sortBy: sortBy }
    if (sortType) condition = { ...condition, sortType: sortType }

    try {
      const { headers, data } = await ReportApi.reportAccounting(condition)
      setRows(data.rows || [])
      setOverdue(data.overdue)
      setRowsExport(data.rowsSummery || [])
      setRowCount(Number(headers['x-total']) || 0)
    } catch (e) {
      console.error('Error fetching accounting data:', e)
      setRows([])
      setRowsExport([])
      setRowCount(0)
    } finally {
      setLoading(false)
      setFetch(false)
    }
  }, [page, pageLimit, branchId, rangeDate, codeForm, billStatus, selectDoctors, patientId, sortBy, sortType, fetch])

  // ดึงข้อมูลสำหรับ export (จำกัด 1000 รายการเพื่อป้องกัน memory issue)
  const fetchDataForExport = useCallback(async () => {
    let condition: any = {
      page: 1,
      pageLimit: 1000, // จำกัดจำนวนเพื่อป้องกัน memory issue
      branchId: branchId,
      dateStart: rangeDate[0],
      dateEnd: rangeDate[1],
      code: codeForm && codeForm.length ? _.map(codeForm, 'value').join(',') : '',
      billStatus: billStatus === 'ALL' ? '' : billStatus,
      export: 1
    }
    if (selectDoctors.length) {
      const doctor = _.filter(selectDoctors, (v: any) => v > 0).join(',')
      if (doctor) condition = { ...condition, doctorId: doctor }
    }
    if (patientId) condition = { ...condition, patientId: patientId }
    if (sortBy) condition = { ...condition, sortBy: sortBy }
    if (sortType) condition = { ...condition, sortType: sortType }

    try {
      const { data } = await ReportApi.reportAccounting(condition)
      setRowsExport(data.rows || [])
      return true
    } catch (e) {
      console.error('Error fetching export data:', e)
      return false
    }
  }, [branchId, rangeDate, codeForm, billStatus, selectDoctors, patientId, sortBy, sortType])

  const exportTableToExcel = async () => {
    // แสดง exporting loading
    setExporting(true)

    try {
      // ดึงข้อมูลทั้งหมดก่อน export
      const success = await fetchDataForExport()

      if (!success) {
        setExporting(false)
        alert('ไม่สามารถดึงข้อมูลได้ กรุณาลองอีกครั้ง')
        return
      }

      // ตั้ง flag ให้ useEffect ทำการ export หลังจาก component render
      setRenderExporting(true)
      setReadyToExport(true)
    } catch (error) {
      console.error('Error preparing export:', error)
      setExporting(false)
      setRenderExporting(false)

      alert('เกิดข้อผิดพลาดในการเตรียมข้อมูล กรุณาลองอีกครั้ง')
    }
  };

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
    setPage(1) // Reset หน้าเป็นหน้า 1 เมื่อ sort
    setFetch(true)
  }

  const handleChangeRowsPerPage = (limit: number) => {
    setPageLimit(limit)
    setPage(1) // Reset หน้าเป็นหน้า 1 เมื่อเปลี่ยนจำนวนรายการต่อหน้า
    setFetch(true)
  }

  const handleChangePage = (val: number) => {
    setPage(val)
    setFetch(true)
  }
  // เรียก API เฉพาะเมื่อ: กดค้นหา, เปลี่ยนหน้า, เปลี่ยน pageLimit, หรือ sort
  useEffect(() => {
    if (!fetch) return
    fetchData()
  }, [searchTrigger, page, pageLimit, sortBy, sortType, fetch])
  // }, [searchTrigger, page, pageLimit, sortBy, sortType, fetchData])


  // สร้างตารางสรุปข้อมูล
  const createSummaryData = useCallback((data: any[], overdue: number, billStatus: string) => {
    const summaryRows: any[][] = []

    // คำนวณข้อมูลสรุป
    const uniquePatients = _.uniqBy(data, 'patientId').length
    const uniqueDays = _.uniqBy(data, 'date').length
    const uniqueReceipts = _.uniqBy(data, 'paymentNumber').length

    const dfNet2 = _.sumBy(data, (o: any) => o.statusOrder === "PAID" ? Number(o.dfNet) : 0)
    const amountCash2 = _.sumBy(data, (o: any) => o.statusOrder === "PAID" ? Number(o.amountCash) : 0)
    const amountTransfer2 = _.sumBy(data, (o: any) => o.statusOrder === "PAID" ? Number(o.amountTransfer) : 0)
    const amountCredit2 = _.sumBy(data, (o: any) => o.statusOrder === "PAID" ? Number(o.amountCredit) : 0)
    const amountWallet2 = _.sumBy(data, (o: any) => o.statusOrder === "PAID" ? Number(o.amountWallet) : 0)
    const amountVoucher2 = _.sumBy(data, (o: any) => o.statusOrder === "PAID" ? Number(o.amountVoucher) : 0)
    const amountAdvance2 = _.sumBy(data, (o: any) => o.statusOrder === "PAID" ? Number(o.amountAdvance) : 0)
    const amountTreatmentRight2 = _.sumBy(data, (o: any) => o.statusOrder === "PAID" ? Number(o.amountTreatmentRight) : 0)
    const creditCost2 = _.sumBy(data, (o: any) => o.statusOrder === "PAID" ? Number(o.creditCost) : 0)

    const dfNetCancel2 = _.sumBy(data, (o: any) => o.statusOrder === "CANCEL" ? Number(o.dfNet) : 0)
    const amountCashCancel2 = _.sumBy(data, (o: any) => o.statusOrder === "CANCEL" ? Number(o.amountCash) : 0)
    const amountTransferCancel2 = _.sumBy(data, (o: any) => o.statusOrder === "CANCEL" ? Number(o.amountTransfer) : 0)
    const amountCreditCancel2 = _.sumBy(data, (o: any) => o.statusOrder === "CANCEL" ? Number(o.amountCredit) : 0)
    const amountWalletCancel2 = _.sumBy(data, (o: any) => o.statusOrder === "CANCEL" ? Number(o.amountWallet) : 0)
    const amountVoucherCancel2 = _.sumBy(data, (o: any) => o.statusOrder === "CANCEL" ? Number(o.amountVoucher) : 0)
    const amountAdvanceCancel2 = _.sumBy(data, (o: any) => o.statusOrder === "CANCEL" ? Number(o.amountAdvance) : 0)
    const amountTreatmentRightCancel2 = _.sumBy(data, (o: any) => o.statusOrder === "CANCEL" ? Number(o.amountTreatmentRight) : 0)
    const creditCostCancel2 = _.sumBy(data, (o: any) => o.statusOrder === "CANCEL" ? Number(o.creditCost) : 0)

    const receiptAdvance3 = _.sumBy(data, (o: any) => o.statusOrder === "PAID" ? Number(o.receiptAdvance) : 0)
    const receiptAdvanceCash3 = _.sumBy(data, (o: any) => o.receiptAdvance > 0 ? o.statusOrder === "PAID" ? Number(o.amountCash) : 0 : 0)
    const receiptAdvanceCashCancel3 = _.sumBy(data, (o: any) => o.receiptAdvance > 0 ? o.statusOrder === "CANCEL" ? Number(o.amountCash) : 0 : 0)
    const receiptAdvanceTransfer3 = _.sumBy(data, (o: any) => o.receiptAdvance > 0 ? o.statusOrder === "PAID" ? Number(o.amountTransfer) : 0 : 0)
    const receiptAdvanceCredit3 = _.sumBy(data, (o: any) => o.receiptAdvance > 0 ? o.statusOrder === "PAID" ? Number(o.amountCredit) : 0 : 0)
    const receiptAdvanceWallet3 = _.sumBy(data, (o: any) => o.receiptAdvance > 0 ? o.statusOrder === "PAID" ? Number(o.amountWallet) : 0 : 0)
    const receiptAdvanceVoucher3 = _.sumBy(data, (o: any) => o.receiptAdvance > 0 ? o.statusOrder === "PAID" ? Number(o.amountVoucher) : 0 : 0)

    const amountAdvance4 = _.sumBy(data, (o: any) => o.statusOrder === "PAID" ? Number(o.amountAdvance) : 0)
    const amountAdvanceCash4 = _.sumBy(data, (o: any) => o.statusOrder === "PAID" ? Number(o.amountAdvanceCash) : 0)
    const amountAdvanceCashCancel4 = _.sumBy(data, (o: any) => o.statusOrder === "CANCEL" ? Number(o.amountAdvanceCash) : 0)
    const amountAdvanceTransfer4 = _.sumBy(data, (o: any) => o.statusOrder === "PAID" ? Number(o.amountAdvanceTransfer) : 0)
    const amountAdvanceCredit4 = _.sumBy(data, (o: any) => o.statusOrder === "PAID" ? Number(o.amountAdvanceCredit) : 0)
    const amountAdvanceWallet4 = _.sumBy(data, (o: any) => o.statusOrder === "PAID" ? Number(o.amountAdvanceWallet) : 0)
    const amountAdvanceVoucher4 = _.sumBy(data, (o: any) => o.statusOrder === "PAID" ? Number(o.amountAdvanceVoucher) : 0)

    const amountPaidOverdue5 = _.sumBy(data, (o: any) => o.amountPaidOverdue > 0 ? o.statusOrder === "PAID" ? Number(o.amountPaidOverdue) : 0 : 0)
    const amountCash5 = _.sumBy(data, (o: any) => o.amountPaidOverdue > 0 ? o.statusOrder === "PAID" ? Number(o.amountCash) : 0 : 0)
    const amountTransfer5 = _.sumBy(data, (o: any) => o.amountPaidOverdue > 0 ? o.statusOrder === "PAID" ? Number(o.amountTransfer) : 0 : 0)
    const amountCredit5 = _.sumBy(data, (o: any) => o.amountPaidOverdue > 0 ? o.statusOrder === "PAID" ? Number(o.amountCredit) : 0 : 0)
    const amountWallet5 = _.sumBy(data, (o: any) => o.amountPaidOverdue > 0 ? o.statusOrder === "PAID" ? Number(o.amountWallet) : 0 : 0)
    const amountVoucher5 = _.sumBy(data, (o: any) => o.amountPaidOverdue > 0 ? o.statusOrder === "PAID" ? Number(o.amountVoucher) : 0 : 0)
    const amountAdvance5 = _.sumBy(data, (o: any) => o.amountPaidOverdue > 0 ? o.statusOrder === "PAID" ? Number(o.amountAdvance) : 0 : 0)
    const amountTreatmentRight5 = _.sumBy(data, (o: any) => o.amountPaidOverdue > 0 ? o.statusOrder === "PAID" ? Number(o.amountTreatmentRight) : 0 : 0)
    const amountCashCancel5 = _.sumBy(data, (o: any) => o.statusOrder === "CANCEL" ? Number(o.amountPaidOverdue) : 0)

    const amountCash6 = _.sumBy(data, (o: any) => o.statusOrder === "PAID" && o.orderId !== null && o.stage === 1 ? Number(o.amountCash) : 0)
    const amountCashCancel6 = _.sumBy(data, (o: any) => o.statusOrder === "CANCEL" && o.orderId !== null && o.stage === 1 ? Number(o.amountPaid) : 0)
    const amountTransfer6 = _.sumBy(data, (o: any) => o.statusOrder === "PAID" && o.orderId !== null && o.stage === 1 ? Number(o.amountTransfer) : 0)
    const amountCredit6 = _.sumBy(data, (o: any) => o.statusOrder === "PAID" && o.orderId !== null && o.stage === 1 ? Number(o.amountCredit) : 0)
    const amountWallet6 = _.sumBy(data, (o: any) => o.statusOrder === "PAID" && o.orderId !== null && o.stage === 1 ? Number(o.amountWallet) : 0)
    const amountVoucher6 = _.sumBy(data, (o: any) => o.statusOrder === "PAID" && o.orderId !== null && o.stage === 1 ? Number(o.amountVoucher) : 0)
    const amountAdvance6 = _.sumBy(data, (o: any) => o.statusOrder === "PAID" && o.orderId !== null && o.stage === 1 ? Number(o.amountAdvance) : 0)
    const amountTreatmentRight6 = _.sumBy(data, (o: any) => o.statusOrder === "PAID" && o.orderId !== null && o.stage === 1 ? Number(o.amountTreatmentRight) : 0)

    const amountCash7 = _.sumBy(data, (o: any) => o.statusOrder === "PAID" ? Number(o.amountCash) : 0)
    const amountTransfer7 = _.sumBy(data, (o: any) => o.statusOrder === "PAID" ? Number(o.amountTransfer) : 0)
    const amountCredit7 = _.sumBy(data, (o: any) => o.statusOrder === "PAID" ? Number(o.amountCredit) : 0)
    const amountWallet7 = _.sumBy(data, (o: any) => o.statusOrder === "PAID" ? Number(o.amountWallet) : 0)
    const amountVoucher7 = _.sumBy(data, (o: any) => o.statusOrder === "PAID" ? Number(o.amountVoucher) : 0)
    const amountTreatmentRight7 = _.sumBy(data, (o: any) => o.statusOrder === "PAID" ? Number(o.amountTreatmentRight) : 0)

    let item: any = _.values(_.mapValues(_.groupBy(_.filter(data, (o) => o.statusOrder === 'PAID' && o.orderId !== null), "orderId"), (group) => _.maxBy(group, "stage").orderPaymentId))
    item = _.filter(data, (o) => item.includes(o.orderPaymentId))
    const balance7 = _.sumBy(item, 'balance')

    // เพิ่มตารางสรุป
    summaryRows.push(['']) // บรรทัดว่าง
    summaryRows.push(['']) // บรรทัดว่าง

    // ตารางที่ 1: จำนวนผู้ป่วย, วัน, ใบเสร็จ และยอดรวม DF/CF/Credit
    summaryRows.push(['จำนวนผู้ป่วย', uniquePatients, '', '', 'ยอดรวม DF', numberFormat(billStatus === 'CANCEL' ? dfNetCancel2 : dfNet2)])
    summaryRows.push(['จำนวนวัน', uniqueDays, '', '', 'ยอดรวม CF', numberFormat(billStatus === 'CANCEL' ? (((amountCashCancel2 + amountTransferCancel2 + amountCreditCancel2 + amountWalletCancel2 + amountVoucherCancel2 + amountAdvanceCancel2 + amountTreatmentRightCancel2) - dfNetCancel2) - creditCostCancel2) || 0 : (((amountCash2 + amountTransfer2 + amountCredit2 + amountWallet2 + amountVoucher2 + amountAdvance2 + amountTreatmentRight2) - dfNet2) - creditCost2) || 0)])
    summaryRows.push(['จำนวนใบเสร็จ', numberFormat(uniqueReceipts, 0, 0), '', '', 'ยอดรวม Credit', numberFormat(billStatus === 'CANCEL' ? creditCostCancel2 : creditCost2 || 0)])

    summaryRows.push(['']) // บรรทัดว่าง

    // ตารางที่ 2: Advance
    summaryRows.push(['ยอดรับเงิน Advance', numberFormat(billStatus === 'CANCEL' ? receiptAdvanceCashCancel3 : receiptAdvance3), '', '', 'ยอดใช้เงิน Advance', numberFormat(billStatus === "CANCEL" ? amountAdvanceCashCancel4 : amountAdvance4)])
    summaryRows.push(['เงินสด (THB)', numberFormat(billStatus === 'CANCEL' ? receiptAdvanceCashCancel3 : receiptAdvanceCash3), '', '', 'เงินสด (THB)', numberFormat(billStatus === 'CANCEL' ? amountAdvanceCashCancel4 : amountAdvanceCash4)])
    summaryRows.push(['เงินโอน (THB)', numberFormat(receiptAdvanceTransfer3), '', '', 'เงินโอน (THB)', numberFormat(amountAdvanceTransfer4)])
    summaryRows.push(['วอลเล็ท (THB)', numberFormat(receiptAdvanceWallet3), '', '', 'วอลเล็ท (THB)', numberFormat(amountAdvanceWallet4)])
    summaryRows.push(['บัตรเครดิต (THB)', numberFormat(receiptAdvanceCredit3), '', '', 'บัตรเครดิต (THB)', numberFormat(amountAdvanceCredit4)])
    summaryRows.push(['Voucher (THB)', numberFormat(receiptAdvanceVoucher3), '', '', 'Voucher (THB)', numberFormat(amountAdvanceVoucher4)])

    summaryRows.push(['']) // บรรทัดว่าง

    // ตารางที่ 3: จ่ายค้างชำระและจ่ายค่ารักษา
    summaryRows.push(['ยอดรวมจ่ายค้างชำระ', numberFormat(billStatus === 'CANCEL' ? amountCashCancel5 : amountPaidOverdue5 || 0), '', '', 'ยอดรวมจ่ายค่ารักษา', numberFormat(billStatus === 'CANCEL' ? amountCashCancel6 : amountCash6 + amountTransfer6 + amountCredit6 + amountWallet6 + amountVoucher6 + amountAdvance6 + amountTreatmentRight6)])
    summaryRows.push(['เงินสด (THB)', numberFormat(billStatus === 'CANCEL' ? amountCashCancel5 : amountCash5 || 0), '', '', 'เงินสด (THB)', numberFormat(billStatus === 'CANCEL' ? amountCashCancel6 : amountCash6)])
    summaryRows.push(['เงินโอน (THB)', numberFormat(amountTransfer5 || 0), '', '', 'เงินโอน (THB)', numberFormat(amountTransfer6)])
    summaryRows.push(['วอลเล็ท (THB)', numberFormat(amountWallet5 || 0), '', '', 'วอลเล็ท (THB)', numberFormat(amountWallet6)])
    summaryRows.push(['บัตรเครดิต (THB)', numberFormat(amountCredit5 || 0), '', '', 'บัตรเครดิต (THB)', numberFormat(amountCredit6)])
    summaryRows.push(['Voucher (THB)', numberFormat(amountVoucher5 || 0), '', '', 'Voucher (THB)', numberFormat(amountVoucher6)])
    summaryRows.push(['สิทธิการรักษา (THB)', numberFormat(amountTreatmentRight5 || 0), '', '', 'สิทธิการรักษา (THB)', numberFormat(amountTreatmentRight6)])
    summaryRows.push(['จ่ายด้วย Advance (THB)', numberFormat(amountAdvance5 || 0), '', '', 'จ่ายด้วย Advance (THB)', numberFormat(amountAdvance6)])

    summaryRows.push(['']) // บรรทัดว่าง

    // ตารางที่ 4: ยอดรับหน้าร้าน
    summaryRows.push(['ยอดรับหน้าร้าน', numberFormat(amountCash7 + amountTransfer7 + amountWallet7 + amountCredit7 + amountVoucher7 + amountTreatmentRight7 || 0)])
    summaryRows.push(['เงินสด (THB)', numberFormat(amountCash7 || 0)])
    summaryRows.push(['เงินโอน (THB)', numberFormat(amountTransfer7 || 0)])
    summaryRows.push(['วอลเล็ท (THB)', numberFormat(amountWallet7 || 0)])
    summaryRows.push(['บัตรเครดิต (THB)', numberFormat(amountCredit7 || 0)])
    summaryRows.push(['Voucher (THB)', numberFormat(amountVoucher7 || 0)])
    summaryRows.push(['สิทธิการรักษา (THB)', numberFormat(amountTreatmentRight7 || 0)])

    summaryRows.push(['']) // บรรทัดว่าง

    // ตารางที่ 5: ค้างชำระ
    summaryRows.push(['ค้างชำระ (THB)', numberFormat(balance7 || 0)])
    summaryRows.push([`ค้างชำระคงเหลือปัจจุบัน (THB) ${dateToView()}`, numberFormat(overdue || 0)])

    return summaryRows
  }, [])

  const handleClear = () => setPatientId(0)

  // Memoize filter object เพื่อป้องกันการสร้าง object ใหม่ทุกครั้ง
  const filterMemo = useMemo(() => ({
    branch: `${_.get(_.find(branches, { branchId: branchId }), 'branchName') || _.get(_.find(branches, { branchId: branchId }), 'branchNameEn')}`,
    rangeDate: rangeDate,
    doctor: getFilterUser(),
    patient: patientFilter,
    billStatus: billStatus === 'ALL' ? t(`REPORT.FILTER.ALL`) : t(`REPORT.FILTER.BILL.${billStatus}`),
    code: codeForm && codeForm.length ? _.map(codeForm, 'value').join(',') : '',
  }), [branches, branchId, rangeDate, getFilterUser, patientFilter, billStatus, codeForm, t])

  // ใช้ข้อมูลสำหรับ export (ถ้ามี rowsExport ใช้ rowsExport ไม่งั้นใช้ rows)
  const dataForExport = useMemo(() => {
    return rowsExport.length > 0 ? rowsExport : rows
  }, [rowsExport, rows])

  // Export เมื่อข้อมูลพร้อม - เขียนใหม่เพื่อประสิทธิภาพสูงและได้ผลลัพธ์ตาม print-show
  useEffect(() => {
    if (readyToExport) {
      const timer = setTimeout(() => {
        try {
          const allRows: any[][] = []

          // 1. เพิ่มข้อมูล filter ด้านบน
          allRows.push(['Accounting Report'])
          allRows.push([
            t("REPORT.FILTER.BRANCH"), filterMemo.branch,
            t('REPORT.FILTER.DATE_START'), filterMemo.rangeDate[0],
            t('REPORT.FILTER.DATE_END'), filterMemo.rangeDate[1],
            t('REPORT.FILTER.DOCTOR'), filterMemo.doctor
          ])
          allRows.push([
            t("REPORT.FILTER.PATIENT"), filterMemo.patient,
            t('REPORT.FILTER.RECEIPT_STATUS'), filterMemo.billStatus,
            t('Code'), filterMemo.code || '-'
          ])
          allRows.push(['']) // บรรทัดว่าง

          // 2. เพิ่ม Headers
          const headers = [
            t('REPORT.TABLE.DF.#NO'),
            'วันที่',
            'เลขที่ใบเสร็จ',
            'สถานะใบเสร็จ',
            'รหัสผู้ป่วย',
            'รหัสผู้ป่วยในสาขา',
            'รหัสผู้ป่วยพิเศษ', // ต้องการลบ อันนี้ หากเข้าเงื่อนไข
            'ชื่อผู้ป่วย',
            'ทันตแพทย์',
            'Code',
            'รายการ',
            'ราคาต่อหน่วย',
            'จำนวน',
            'ราคาค่ารักษา',
            'DF%',
            'ยอดค่ารักษา DF',
            'CF%',
            'ยอดค่ารักษา CF',
            'ส่วนลดโปรโมชั่น DF',
            'ส่วนลดโปรโมชั่น CF',
            'Total Discount',
            'ยอดรวมสุทธิ DF',
            'ยอดรวมสุทธิ CF',
            'ยอดรวมสุทธิค่ารักษา',
            'จ่ายค่ารักษา',
            'จ่ายค้างชำระ',
            'Advance',
            'ยอดรับเงินสุทธิ',
            'เงินสด',
            'เงินโอน',
            'วอลเล็ท',
            'เครดิต',
            'Voucher',
            'สิทธิการรักษา',
            'Clear Advance',
            'Adv เงินสด',
            'Adv เงินโอน',
            'Adv วอลเล็ท',
            'Adv เครดิต',
            'Adv Voucher',
            'ยอดรวมเครดิต',
            'Credit%',
            'DF%',
            'Clinic%',
            'ยอดรวมเครดิต%',
            'DF สุทธิ',
            'Clinic สุทธิ',
            'ค้างชำระ DF',
            'ค้างชำระ CF',
            'Balance',
            'ผู้บันทึก',
            'รายละเอียด',
            'หมายเหตุการยกเลิก',
            'ผู้ยกเลิก',
          ]
          if (!snType) {
            headers.splice(6, 1)
          }
          allRows.push(headers)

          // 3. เพิ่มข้อมูลแต่ละแถว (ประมวลผลแบบ chunk)
          const CHUNK_SIZE = 5000
          for (let i = 0; i < rowsExport.length; i += CHUNK_SIZE) {
            const chunk = rowsExport.slice(i, i + CHUNK_SIZE)
            chunk.forEach((accounting: any, chunkIndex: number) => {
              const rowIndex = i + chunkIndex
              const code = accounting.code ? accounting.code : '-'
              const itemName = accounting.itemName ? accounting.itemName : ''
              const remark = accounting.remark ? accounting.remark.replace('<br/>', '\n') : ''

              const data = [
                rowIndex + 1,
                dateTimeToView(accounting.createdAt),
                accounting.paymentNumber || '',
                t(`REPORT.BILL_STATUS.${accounting.statusOrder}`) || '',
                accounting.cnNumber || '',
                accounting.branchCnNumber || '',
                accounting.snCode || '',
              ]
              if (!snType) data.pop();
              allRows.push([
                ...data,
                accounting.patientFullName || '',
                accounting.doctorFullName || '',
                code,
                itemName,
                accounting.price || 0,
                accounting.qty || 0,
                accounting.totalOriginal || 0,
                accounting.dfPercentOriginal || 0,
                accounting.dfBahtOriginal || 0,
                accounting.cfPercentOriginal || 0,
                accounting.cfBahtOriginal || 0,
                accounting.dfDiscount || 0,
                accounting.cfDiscount || 0,
                accounting.discountTotal || 0,
                accounting.dfNetOriginal || 0,
                accounting.cfNetOriginal || 0,
                accounting.total || 0,
                accounting.amountPaid || 0,
                accounting.amountPaidOverdue || 0,
                accounting.receiptAdvance || 0,
                (accounting.amountPaid || 0) + (accounting.amountPaidOverdue || 0) + (accounting.receiptAdvance || 0),
                accounting.amountCash || 0,
                accounting.amountTransfer || 0,
                accounting.amountWallet || 0,
                accounting.amountCredit || 0,
                accounting.amountVoucher || 0,
                accounting.amountTreatmentRight || 0,
                accounting.amountAdvance || 0,
                accounting.amountAdvanceCash || 0,
                accounting.amountAdvanceTransfer || 0,
                accounting.amountAdvanceWallet || 0,
                accounting.amountAdvanceCredit || 0,
                accounting.amountAdvanceVoucher || 0,
                (accounting.amountAdvanceCredit || 0) + (accounting.amountCredit || 0),
                accounting.creditCardFee || 0,
                accounting.dfCreditCost || 0,
                accounting.cfCreditCost || 0,
                (accounting.dfCreditCost || 0) + (accounting.cfCreditCost || 0),
                accounting.dfNet || 0,
                accounting.cfNet || 0,
                accounting.overdueDf || 0,
                accounting.overdueCf || 0,
                accounting.balance || 0,
                accounting.staff || '',
                remark,
                accounting.cancelRemark || '',
                accounting.cancelBy || '',
              ])
            })
          }

          // 4. เพิ่มแถว Sum


          const sumRow: any[] = [
            '', '', '', '', '', '', '', '', '', '',
          ];
          if (!snType) sumRow.pop();
          allRows.push([
            ...sumRow,
            t('REPORT.TITLE.SUM'),
            _.sumBy(rowsExport, 'price') || 0,
            _.sumBy(rowsExport, 'qty') || 0,
            _.sumBy(rowsExport, 'totalOriginal') || 0,
            _.sumBy(rowsExport, 'dfPercentOriginal') || 0,
            _.sumBy(rowsExport, 'dfBahtOriginal') || 0,
            _.sumBy(rowsExport, 'cfPercentOriginal') || 0,
            _.sumBy(rowsExport, 'cfBahtOriginal') || 0,
            _.sumBy(rowsExport, 'dfDiscount') || 0,
            _.sumBy(rowsExport, 'cfDiscount') || 0,
            _.sumBy(rowsExport, 'discountTotal') || 0,
            _.sumBy(rowsExport, 'dfNetOriginal') || 0,
            _.sumBy(rowsExport, 'cfNetOriginal') || 0,
            _.sumBy(rowsExport, 'total') || 0,
            _.sumBy(rowsExport, 'amountPaid') || 0,
            _.sumBy(rowsExport, 'amountPaidOverdue') || 0,
            _.sumBy(rowsExport, 'receiptAdvance') || 0,
            _.sumBy(rowsExport, 'amountPaid') + _.sumBy(rowsExport, 'amountPaidOverdue') + _.sumBy(rowsExport, 'receiptAdvance'),
            _.sumBy(rowsExport, 'amountCash') || 0,
            _.sumBy(rowsExport, 'amountTransfer') || 0,
            _.sumBy(rowsExport, 'amountWallet') || 0,
            _.sumBy(rowsExport, 'amountCredit') || 0,
            _.sumBy(rowsExport, 'amountVoucher') || 0,
            _.sumBy(rowsExport, 'amountTreatmentRight') || 0,
            _.sumBy(rowsExport, 'amountAdvance') || 0,
            _.sumBy(rowsExport, 'amountAdvanceCash') || 0,
            _.sumBy(rowsExport, 'amountAdvanceTransfer') || 0,
            _.sumBy(rowsExport, 'amountAdvanceWallet') || 0,
            _.sumBy(rowsExport, 'amountAdvanceCredit') || 0,
            _.sumBy(rowsExport, 'amountAdvanceVoucher') || 0,
            _.sumBy(rowsExport, 'amountAdvanceCredit') + _.sumBy(rowsExport, 'amountCredit'),
            _.sumBy(rowsExport, 'creditCardFee') || 0,
            _.sumBy(rowsExport, 'dfCreditCost') || 0,
            _.sumBy(rowsExport, 'cfCreditCost') || 0,
            (_.sumBy(rowsExport, 'dfCreditCost') || 0) + (_.sumBy(rowsExport, 'cfCreditCost') || 0),
            _.sumBy(rowsExport, 'dfNet') || 0,
            _.sumBy(rowsExport, 'cfNet') || 0,
            _.sumBy(rowsExport, 'overdueDf') || 0,
            _.sumBy(rowsExport, 'overdueCf') || 0,
            _.sumBy(rowsExport, 'balance') || 0,
            '', '', '', ''
          ])

          // 5. เพิ่มตารางสรุป
          const summaryData = createSummaryData(rowsExport, overdue, billStatus)
          allRows.push(...summaryData)

          // 6. สร้าง worksheet
          const worksheet = XLSX.utils.aoa_to_sheet(allRows)

          // 7. Format code column เป็น text
          const codeColIndex = 9 // Column J (0-indexed, เพราะมี filter rows ด้านบน)
          const headerRowOffset = 5
          for (let i = 0; i < rowsExport.length; i++) {
            const cellRef = XLSX.utils.encode_cell({ r: i + headerRowOffset, c: codeColIndex })
            if (worksheet[cellRef]) {
              worksheet[cellRef].z = '@' // Format as text
            }
          }

          // ระบุ Index ของคอลัมน์ที่เป็นตัวเลขทั้งหมด (เริ่มนับจาก 0)
          const numberColumnIndices = [
            11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27,
            28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44,
            45, 46, 47, 48, 49
          ]

          // const numberFormatString = '#,##0.00'
          const totalDataRows = rowsExport.length;
          const sumRowIndex = headerRowOffset + totalDataRows; // แถวของ Sum

          // วนลูปทุกแถวข้อมูล + แถว Sum
          for (let r = headerRowOffset; r <= sumRowIndex; r++) {
            for (const c of numberColumnIndices) {
              const cellRef = XLSX.utils.encode_cell({ r: r, c: c })
              if (worksheet[cellRef]) {
                worksheet[cellRef].t = 'n' // 'n' = Number Type
                // worksheet[cellRef].z = numberFormatString // .z = Format String
              }
            }
          }

          // 8. Export
          forceTextOnDateCellsXlsx(worksheet)
          const workbook = XLSX.utils.book_new()
          XLSX.utils.book_append_sheet(workbook, worksheet, "Accounting Report")
          XLSX.writeFile(workbook, "Accounting Report.xlsx")
        } catch (error) {
          console.error('Error exporting to Excel:', error)
          alert('เกิดข้อผิดพลาดในการ export ข้อมูล กรุณาลองอีกครั้ง')
        } finally {
          setRenderExporting(false)
          setExporting(false)
          setReadyToExport(false)
        }
      }, 100)

      return () => clearTimeout(timer)
    }
  }, [readyToExport, rowsExport, t, filterMemo, overdue, billStatus, createSummaryData])


  const handleFilterDoctors = (value: any) => {
    if ((value === 'ALL' && _.size(selectDoctors.filter((item) => typeof item === 'number')) === _.size(doctors)) || value === 'CLEAR') setSelectDoctors([''])
    else if (value === 'ALL') setSelectDoctors([..._.map(doctors, (d: any) => d.userId)])
    else if (_.includes(selectDoctors, value)) setSelectDoctors(_.filter(selectDoctors, (d) => d !== value))
    else setSelectDoctors([...selectDoctors, value])
  }

  return (
    <Box>
      <HeaderCard text={t(`Accounting Report`)} />
      <Box px={3} pt={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <SelectInput
              label={t('REPORT.FILTER.BRANCH')}
              value={branchId}
              onChange={e => setBranchId(Number(e.target.value))}
              options={[
                ...branches.map(b => ({ value: b.branchId, name: b.branchName || b.branchNameEn }))
              ]}
              width="100%"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <InputSecondNewRangePicker
              inputHeight={41}
              value={rangeDate || emptyRangePicker}
              onchange={(val: any) => {
                if (_.isEmpty(val)) setRangeDate(emptyRangePicker)
                else setRangeDate([moment(val[0]).format('YYYY-MM-DD'), moment(val[1]).format('YYYY-MM-DD')])
              }}
              label={''}
            //  allowClear
            // onClear={() => setRangeDate(emptyRangePicker)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <ClickAwayListener onClickAway={() => setOpenFilterDoctors(false)}>
              <CustomFilterDoctor open={openFilterDoctors} onClick={() => setOpenFilterDoctors(true)}>
                <AutocompleteSelect
                  open={openFilterDoctors}
                  multiple
                  labelId="label-doctors"
                  options={['ALL', ...doctors, 'CLEAR']}
                  getOptionLabel={(option) => _.find(doctors, { userId: option.userId })?.fullname || '-'}
                  renderOption={(props, option) => {
                    if (option === 'ALL') {
                      return (
                        <ListFilterDoctors {...props} key={option} component="li" value={option}>
                          <InputCheckbox
                            label={t('REPORT.REPORT_GROUP.ALL')}
                            checked={_.size(selectDoctors.filter((item) => typeof item === 'number')) === _.size(doctors)}
                            indeterminate={_.size(selectDoctors) > 1 && _.size(selectDoctors.filter((item) => typeof item === 'number')) !== _.size(doctors)}
                            className={'pe-none'}
                          />
                        </ListFilterDoctors>
                      )
                    } else if (option === 'CLEAR') {
                      return (
                        <Box {...props} key={option} component="li" value={option} sx={{ borderTop: `1px solid ${colors.lightGray}` }}>
                          <UseStyled.SpanClearFilter className="pe-none">{t('REPORT.FILTER.CLEAR_FILTER')}</UseStyled.SpanClearFilter>
                        </Box>
                      )
                    } else {
                      return (
                        <ListFilterDoctors {...props} key={option.userId} component="li" value={option.userId}>
                          <InputCheckbox label={option.fullname} checked={_.includes(selectDoctors, option.userId)} className={'pe-none'} />
                        </ListFilterDoctors>
                      )
                    }
                  }}
                  renderTags={(tagValue, getTagProps) => {
                    return (
                      <>
                        <span className="pr-1">{t('USER_DOCTOR')}:</span>
                        {!openFilterDoctors &&
                          ((_.size(tagValue.filter((item: any) => typeof item === 'number')) === _.size(doctors) && <span>{t('REPORT.REPORT_GROUP.ALL')}</span>) || (
                            <Box className="text-ellipsis">
                              {_.map(tagValue, (option: any, index: number) => {
                                return (
                                  <span>
                                    {_.find(doctors, { userId: option })?.fullname}
                                    {index !== _.findLastIndex(tagValue) && option && `,`}
                                  </span>
                                )
                              })}
                            </Box>
                          ))}
                      </>
                    )
                  }}
                  onchange={(e, value) => handleFilterDoctors(e?.target?.value || e?.target?.attributes?.value?.value)}
                  noOptionsText={t('NOT_FOUND')}
                  filterOptions={(option) => 'ALL' + option.fullname + 'CLEAR'}
                  value={selectDoctors}
                  height={40}
                  disableClearable
                />
              </CustomFilterDoctor>
            </ClickAwayListener>
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={3}>
            <SearchPatients
              status="ACTIVE"
              callbackPatientId={setPatientId}
              patientId={patientId}
              onClear={handleClear}
              callbackPatientName={(value) => {
                setPatientFilter(value)
              }}
              bodyHeight={41}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <SelectInput
              label={t('REPORT.FILTER.RECEIPT_STATUS')}
              value={billStatus}
              onChange={e => {
                setBillStatus(e.target.value as string)
                setPage(1)
              }}
              options={[
                { value: 'ALL', name: t('REPORT.FILTER.ALL') },
                { value: 'NORMAL', name: t('REPORT.FILTER.BILL.NORMAL') },
                { value: 'CANCEL', name: t('REPORT.FILTER.BILL.CANCEL') },
              ]}
              width="100%"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3} xl={2}>
            <GoogleExcelSelectSearch
              labelId="patients-multi-select"
              inputLabel="Code"
              data={[..._.map(_.uniq(operativeCode), (item) => ({ id: item.productCode, label: item.productCode, value: item.productCode }))] as any}
              columns={['value']}
              multiSelect={true}
              height={40}
              disableClearable={true}
              selectedRows={codeForm || []}
              onSelectionChange={(rows: any) => setCodeForm(rows)}
              onClearAll={() => setCodeForm([])}
              onSelectAll={() => setCodeForm([..._.map(_.uniq(operativeCode), (item) => ({ id: item.productCode, label: item.productCode, value: item.productCode }))] as any)}
              limitTags={1}
              maxResults={_.uniq(operativeCode).length + 1}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3} xl={2}>
            <MainButton
              title={t('INPUT.SEARCH')}
              type="primary"
              variant="contained"
              style={{ width: '120px' }}
              onClick={() => {
                setFetch(true)
                setPage(1) // Reset หน้าเป็นหน้า 1
                setSearchTrigger(prev => prev + 1) // เพิ่ม trigger เพื่อเรียก API
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={8} display="flex" justifyContent="flex-end">
            <UseStyled.DropdownDocument>
              <Dropdown.Toggle id="dropdown-basic" className="ml-xl-auto">
                {t('REGISTER.BUTTON.PRINT_DOCUMENT')}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={exportTableToExcel}>{t('Excel')}</Dropdown.Item>
              </Dropdown.Menu>
            </UseStyled.DropdownDocument>
          </Grid>
        </Grid>
        <Box className={'mt-3 pl-xl- pr-xl-2 pb-4'}>
          <AccountingList
            page={page}
            pageLimit={pageLimit}
            rowCount={rowCount}
            sortBy={sortBy}
            sortType={sortType}
            data={rows}
            overdue={overdue}
            dataExport={dataForExport}
            onRequestSort={onRequestSort}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            handleChangePage={handleChangePage}
            setSortType={setSortType}
            setSortBy={setSortBy}
            componentRef={componentRef}
            exporting={false}
            filter={filterMemo}
            billStatus={billStatus}
          />
        </Box>
      </Box>
      <Loading show={loading} type="fullLoading" />
      {exporting && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
        >
          <Box sx={{ backgroundColor: 'white', padding: 3, borderRadius: 2, textAlign: 'center' }}>
            <Typography variant="h6">{t('กำลังเตรียมข้อมูลสำหรับ Export...')}</Typography>
          </Box>
        </Box>
      )}
    </Box >
  )
}
